
import {throwError as observableThrowError, Observable, Subscriber, of } from 'rxjs';
import { Injectable } from '@angular/core';
import { PartnerService } from './partner.service';
import { Partner } from '../models/partner';
import { LoginStateService } from '../../login/services/login-state.service';
import { User } from '../../login/models/user';
import { LoginService } from '../../login/services/login.service';
import { LoginInMemoryService } from '../../login/services/login-in-memory.service';
import { JSONUtilites } from '../../../helpers/JSONUtilites';
import { PartnerRelationship } from '../models/parent-relationship';
import { InMemoryPartners } from '../models/in-memory-partners';
import { InMemoryPartnerRelationships } from '../models/in-memory-partner-relationship';
import { RemovePartnerResult } from '../models/remove-partner-result';

@Injectable()
export class PartnerInMemoryService extends PartnerService {

  private _inMemoryPartners = new InMemoryPartners();
  private _inMemoryPartnerRelationships = new InMemoryPartnerRelationships();

  constructor(
    private loginStateService: LoginStateService,
    private loginService: LoginService,
  ) {
    super();
  }

  get loggedInPartner(): Observable<Partner> {
    return Observable.create(
      (observable: Subscriber<Partner>) => {
        const user = this.loginStateService.loggedInUser;
        observable.next(user ? this._inMemoryPartners.get(user.partner_id) : null);
      }
    );
  }

  getPartnerById(partner_id: number): Observable<Partner> {
    return of(this._inMemoryPartners.get(partner_id));
  }

  createPartner(partner: Partner, password: string): Observable<Partner> {
    partner.id = this._newPartnerId;
    this._addPartner(partner);
    this._savePartners();

    try {
      const loginInMemoryService = this.loginService as LoginInMemoryService;
      loginInMemoryService.createUser(
        new User(
          {
            username: partner.firstName,
            login: partner.email,
            password: password,
            partner_id: partner.id
          }));
      return of(partner);
    } catch (e) {
      return observableThrowError(e);
    }
  }

  updatePartner(partner: Partner): Observable<Partner> {
    this._updatePartner(partner);
    this._savePartners();
    return of(partner);
  }

  private _updatePartner(partner: Partner) {
    const index = this._inMemoryPartners.items.findIndex(x => x.id === partner.id);
    this._inMemoryPartners.items[index] = partner;
  }

  getChildren(): Observable<Partner[]> {
    return Observable.create(
      (observable: Subscriber<Partner[]>) => {
        const user = this.loginStateService.loggedInUser;
        if (!user) {
          observable.next(null);
          return;
        }
        observable.next(this._getChildren(user.partner_id));
      }
    );
  }

  private _getChildren(partnerId: number): Partner[] {
    const childrenIds = this._inMemoryPartnerRelationships.get(partnerId);
    if (!childrenIds || !childrenIds.childrenIds) {
      return null;
    }

    const children = new Array<Partner>();
    childrenIds.childrenIds.forEach(
      childId => {
        const child = this._inMemoryPartners.get(childId);
        if (child) {
          children.push(child);
        }
      });

    return children;
  }

  createChild(child: Partner): Observable<Partner> {
    return Observable.create((observer: Subscriber<Partner>) => {
      return this.loggedInPartner.subscribe(
        partner => {

          if (!partner) {
            observer.error(Error('Partner not logged on'));
            return;
          }

          observer.next(this._createChild(child, partner));
        },
        error => {
          observer.error(error);
        },
        () => {
          observer.complete();
        });
    });
  }

  updateChild(child: Partner): Observable<Partner> {
    return Observable.create((observer: Subscriber<Partner>) => {
      return this.loggedInPartner.subscribe(
        partner => {

          if (!partner) {
            observer.error(Error('Partner not logged on'));
            return;
          }
          try {
            observer.next(this._updateChild(child, partner));
          } catch (e) {
            observer.error(e);
          }
        },
        error => {
          observer.error(error);
        },
        () => {
          observer.complete();
        });
    });
  }

  removeChild(child: Partner): Observable<boolean> {
    return Observable.create((observer: Subscriber<boolean>) => {
      return this.loggedInPartner.subscribe(
        partner => {

          if (!partner) {
            observer.error(Error('Partner not logged on'));
            return;
          }
          try {
            observer.next(this._removeChild(child, partner));
          } catch (e) {
            observer.error(e);
          }
        },
        error => {
          observer.error(error);
        },
        () => {
          observer.complete();
        });
    });
  }

  private _createChild(child: Partner, partner: Partner): Partner {
    if (!partner) {
      return null;
    }

    const children = this._getChildren(partner.id);
    if (children) {
      const foundChild = children.find(x => child.isChildEquals(x));
      if (foundChild) {
        return foundChild;
      }
    }

    child.id = this._newPartnerId;
    this._addPartner(child);
    this._savePartners();

    let partnerRelationship = this._inMemoryPartnerRelationships.get(partner.id);
    if (partnerRelationship == null) {
      partnerRelationship = new PartnerRelationship({ partnerId: partner.id, childrenIds: [] });
      this._inMemoryPartnerRelationships.items.push(partnerRelationship);
    }
    partnerRelationship.childrenIds.push(child.id);
    this._savePartnerRelationship();
    return child;
  }

  private _updateChild(child: Partner, partner: Partner): Partner {
    if (!partner) {
      throw Error('Child not found');
    }

    const children = this._getChildren(partner.id);
    if (!children) {
      throw Error('Child not found');
    }

    const childIndex = children.findIndex(x => x.id === child.id);
    if (childIndex === -1) {
      throw Error('Child not found');
    }

    this._updatePartner(child);
    this._savePartners();

    return child;
  }

  private _removeChild(child: Partner, partner: Partner): boolean {
    if (!partner) {
      throw Error('Child not found');
    }

    const children = this._getChildren(partner.id);
    if (!children) {
      throw Error('Child not found');
    }

    const childIndex = children.findIndex(x => x.id === child.id);
    if (childIndex === -1) {
      throw Error('Child not found');
    }

    const partnerRelationship = this._inMemoryPartnerRelationships.get(partner.id);
    if (partnerRelationship && partnerRelationship.childrenIds && partnerRelationship.childrenIds.length) {
      const foundChildIndex = partnerRelationship.childrenIds.findIndex(x => x === child.id);
      partnerRelationship.childrenIds.splice(foundChildIndex, 1);
      this._savePartnerRelationship();
    }

    return true;
  }

  private get _newPartnerId(): number {
    return this._inMemoryPartners.newItemId;
  }

  private _addPartner(partner: Partner) {
    this._inMemoryPartners.items.push(partner);
  }

  private _savePartners() {
    this._inMemoryPartners.save();
  }

  private _savePartnerRelationship() {
    this._inMemoryPartnerRelationships.save();
  }

  removeLoggedInPartner(password: string): Observable<RemovePartnerResult> {
    const removePartnerResult = new RemovePartnerResult();
    removePartnerResult.result = true;
    return of(removePartnerResult);
  }
}
